import React, { useEffect, useMemo, useState } from "react";
import VirtualizedNewsList from "../news/VirtualizedNewsList";
import FirstAdCard from "../ads/FirstAdCard";
import useFocusInitialVisualReady from "./useFocusInitialVisualReady";
import useAdPlan from "../../hooks/useAdPlan";
import { createSynchronizedSkeletonStyle } from "../../utils/skeletonMotion";

export const FOCUS_RENDER_RADIUS = 1;
export const DEFAULT_INITIAL_BATCH_SIZE = 20;

export const getFocusPagerItemKey = (item, index = 0) => {
  if (item?.id !== undefined && item?.id !== null) return `id:${item.id}`;
  if (item?.link) return `link:${item.link}`;
  return `fallback:${index}-${item?.title || ""}`;
};

export const FocusPagerSkeleton = () => {
  const synchronizedMotionStyle = useMemo(
    () => createSynchronizedSkeletonStyle(),
    []
  );

  return (
    <div
      className="focus-pager__skeleton"
      aria-hidden="true"
      style={synchronizedMotionStyle}
    >
      {Array.from({ length: 7 }).map((_, index) => (
        <article key={index} className="focus-pager__skeleton-card">
          <span className="focus-pager__skeleton-image focus-pager__skeleton-shine" />
          <div className="focus-pager__skeleton-content">
            <span className="focus-pager__skeleton-title focus-pager__skeleton-shine" />
            <span className="focus-pager__skeleton-title focus-pager__skeleton-title--short focus-pager__skeleton-shine" />
            <span className="focus-pager__skeleton-meta focus-pager__skeleton-shine" />
          </div>
        </article>
      ))}
    </div>
  );
};

const FocusPagerPage = ({
  className,
  style,
  sourceKey,
  sourceNews,
  isPageRendered,
  isActivePage = false,
  favicons,
  onMarkAsRead,
  onNewsOpen,
  scrollToTopSignal,
  onAtTopChange,
  savedLinks,
  onToggleSaved,
  onShare,
  onView,
  onLoadMore,
  onEnsureInitial,
  hasMore = false,
  isLoadingMore = false,
  hasCheckedInitial = false,
  hasInitialError = false,
  initialBatchSize = DEFAULT_INITIAL_BATCH_SIZE,
  focusBatchCount = 0,
}) => {
  const [pageElement, setPageElement] = useState(null);

  const adPlan = useAdPlan({
    enabled: isPageRendered,
    surface: "focus",
    device: "mobile",
    feedKey: `focus:${sourceKey || "unknown"}`,
    newsCount: sourceNews?.length || 0,
  });

  // The page element itself is the scroll container.
  // No inner wrapper. No scrollContainerMode prop.
  //
  // Important: Virtuoso must not mount before this element exists. Mounting it
  // first with window scrolling and then switching to customScrollParent causes
  // a visible blank frame when a previously loaded Focus source is reopened.
  const scrollParent = pageElement;

  const hasEnoughInitialItems = focusBatchCount >= initialBatchSize;

  // Initial readiness must remain latched after the first batch finishes.
  // `isLoadingMore` also becomes true for pagination requests, so using it
  // here would incorrectly replace the existing news list with the full
  // initial skeleton every time the user reaches the end of the list.
  const isInitialBatchReady =
    hasEnoughInitialItems || hasCheckedInitial;

  const needsInitialBatch = !isInitialBatchReady;

  const {
    isVisualReady,
    handleRangeChanged,
  } = useFocusInitialVisualReady({
    sourceKey,
    items: sourceNews,
    favicons,
    enabled:
      isPageRendered &&
      !hasInitialError &&
      Boolean(scrollParent),
    isInitialDataReady: isInitialBatchReady,
    scrollParent,
    initialBatchSize,
  });

  useEffect(() => {
    if (!isPageRendered) return;
    if (!needsInitialBatch) return;
    if (hasInitialError) return;
    if (isLoadingMore) return;

    onEnsureInitial?.();
  }, [
    hasInitialError,
    isLoadingMore,
    isPageRendered,
    needsInitialBatch,
    onEnsureInitial,
  ]);

  // Keep a real visual placeholder until the page-owned scroll container is
  // available. The callback ref updates pageElement before the prepared list is
  // mounted, so Virtuoso receives the correct scroll parent on its first mount.
  if (!isPageRendered || !scrollParent) {
    return (
      <div
        className={className}
        style={style}
        ref={setPageElement}
        data-source-key={sourceKey}
      >
        <FocusPagerSkeleton />
      </div>
    );
  }

  return (
    <div
      className={className}
      style={style}
      ref={setPageElement}
      data-source-key={sourceKey}
    >
      {hasInitialError || needsInitialBatch ? (
        <FocusPagerSkeleton />
      ) : sourceNews.length === 0 ? (
        <div className="status-message">خبری برای این منبع وجود ندارد</div>
      ) : (
        <div
          className={`focus-pager__initial-stage${
            isVisualReady
              ? " focus-pager__initial-stage--ready"
              : " focus-pager__initial-stage--preparing"
          }`}
        >
          <div
            className={`focus-pager__initial-content${
              isVisualReady
                ? ""
                : " focus-pager__initial-content--preparing"
            }`}
            aria-hidden={!isVisualReady}
          >
            <VirtualizedNewsList
              key={`focus-list:${sourceKey}`}
              items={sourceNews}
              favicons={favicons}
              onMarkAsRead={
                isActivePage && isVisualReady
                  ? onMarkAsRead
                  : undefined
              }
              onNewsOpen={onNewsOpen}
              scrollParent={scrollParent || undefined}
              scrollToTopSignal={scrollToTopSignal}
              onAtTopChange={
                isVisualReady ? onAtTopChange : undefined
              }
              savedLinks={savedLinks}
              onToggleSaved={onToggleSaved}
              onShare={onShare}
              onView={onView}
              onLoadMore={isVisualReady ? onLoadMore : undefined}
              hasMore={isVisualReady ? hasMore : false}
              isLoadingMore={isVisualReady ? isLoadingMore : false}
              footerSpacerHeight={88}
              mobileDividerEdge="bottom"
              onRangeChanged={handleRangeChanged}
              eagerImages={!isVisualReady}
              initialItemCount={
                isInitialBatchReady
                  ? Math.min(sourceNews.length, 6)
                  : 0
              }
              headerContent={
                <FirstAdCard
                  ad={adPlan.first}
                  houseAd={adPlan.houseAd}
                  mobileDividerEdge="bottom"
                  surface="focus"
                  device="mobile"
                  analyticsSlot={`focus:${sourceKey}:first`}
                  viewabilityEnabled={isActivePage && isVisualReady}
                />
              }
              regularAdsEvery={adPlan.regularEvery}
              regularAds={adPlan.regular}
              houseAd={adPlan.houseAd}
              adSurface="focus"
              adDevice="mobile"
              adViewabilityEnabled={isActivePage && isVisualReady}
              adAnalyticsSlotPrefix={`focus:${sourceKey}`}
            />
          </div>

          {!isVisualReady && (
            <div className="focus-pager__initial-overlay">
              <FocusPagerSkeleton />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FocusPagerPage;